import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { MapPin, Navigation, X, Check } from "lucide-react";
import { toast } from "sonner";

export default function DeliveryAddressModal({ onClose, onSave, current }) {
  const [form, setForm] = useState({
    pincode:  current?.pincode  || "",
    city:     current?.city     || "",
    area:     current?.area     || "",
    landmark: current?.landmark || "",
  });
  const [detecting, setDetecting] = useState(false);

  // Prevent body scroll when modal is open — simple overflow approach
  // Works on all browsers without scroll-position jump
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const detectGPS = () => {
    if (!navigator.geolocation) return toast.error("GPS is not supported on this device");
    setDetecting(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${pos.coords.latitude}&lon=${pos.coords.longitude}&format=json&accept-language=en`
          );
          const data = await res.json();
          setForm({
            pincode:  data.address?.postcode  || "",
            city:     data.address?.city || data.address?.town || data.address?.village || "",
            area:     data.address?.suburb || data.address?.neighbourhood || data.address?.county || "",
            landmark: "",
          });
          toast.success("Location detected!");
        } catch {
          toast.error("Could not fetch address. Please enter manually.");
        } finally {
          setDetecting(false);
        }
      },
      () => {
        toast.error("GPS access denied. Please enter manually.");
        setDetecting(false);
      }
    );
  };

  const save = () => {
    if (!form.pincode && !form.city) return toast.error("Please enter at least city or pincode");
    onSave(form);
    onClose();
  };

  // createPortal ensures modal renders in document.body DOM node —
  // completely outside topbar's backdrop-filter stacking context
  return createPortal(
    <div
      className="addr-modal-overlay"
      onClick={onClose}
      data-testid="delivery-address-modal"
    >
      <div className="addr-modal" onClick={(e) => e.stopPropagation()}>
        <div className="addr-modal-head">
          <div>
            <MapPin size={18} />
            <h3>Delivery Address</h3>
          </div>
          <button onClick={onClose} data-testid="addr-modal-close"><X size={18} /></button>
        </div>

        <button
          className="gps-detect-btn"
          onClick={detectGPS}
          disabled={detecting}
          data-testid="gps-detect-button"
        >
          <Navigation size={16} />
          {detecting ? "Detecting location…" : "Use Current Location (GPS)"}
        </button>

        <div className="addr-divider"><span>OR enter manually</span></div>

        <div className="addr-fields">
          <input
            value={form.pincode}
            onChange={(e) => set("pincode", e.target.value)}
            placeholder="Pincode (e.g. 110085)"
            data-testid="addr-pincode-input"
            maxLength={6}
          />
          <input
            value={form.city}
            onChange={(e) => set("city", e.target.value)}
            placeholder="City / Town (e.g. New Delhi)"
            data-testid="addr-city-input"
          />
          <input
            value={form.area}
            onChange={(e) => set("area", e.target.value)}
            placeholder="Area / Colony (optional)"
            data-testid="addr-area-input"
          />
          <input
            value={form.landmark}
            onChange={(e) => set("landmark", e.target.value)}
            placeholder="Nearby landmark (optional)"
            data-testid="addr-landmark-input"
          />
        </div>

        <button
          className="primary-btn"
          onClick={save}
          data-testid="addr-save-button"
          style={{ width: "100%" }}
        >
          <Check size={15} /> Save Address
        </button>
      </div>
    </div>,
    document.body
  );
}
